// Role-based statistics interfaces based on backend API response

export interface BaseStatistics {
  subscription_status: "active" | "expired";
  subscription_plan: "basic" | "professional" | "enterprise";
  subscription_expires_at: string;
}

export interface FirmAdminStats extends BaseStatistics {
  overview_type: "admin";
  active_cases: number;
  total_cases: number;
  completed_cases: number;
  total_clients: number;
  active_clients: number;
  team_members: number;
  lawyers: number;
  staff_members: number;
  monthly_revenue: string; // "$1,234.56"
  yearly_revenue: string;  // "$12,345.67"
  success_rate: string;    // "85%"
  average_case_duration: string; // "45 days"
  pending_tasks: number;
  overdue_tasks: number;
  upcoming_hearings: number;
}

export interface LawyerStats extends BaseStatistics {
  overview_type: "lawyer";
  my_active_cases: number;
  my_total_cases: number;
  my_completed_cases: number;
  my_clients: number;
  supervised_cases: number;
  my_success_rate: string;
  my_monthly_revenue: string;
  my_pending_tasks: number;
  my_overdue_tasks: number;
  upcoming_hearings: number;
  average_case_duration: string;
}

export interface ClientStats extends BaseStatistics {
  overview_type: "client";
  my_cases: number;
  active_cases: number;
  completed_cases: number;
  pending_tasks: number;
  upcoming_hearings: number;
  total_bills: number;
  unpaid_bills: number;
  outstanding_balance: string;
  next_hearing_date: string | null;
  message?: string; // If no client record found
}

export interface StaffStats extends BaseStatistics {
  overview_type: "staff";
  my_assigned_tasks: number;
  my_pending_tasks: number;
  my_completed_tasks: number;
  my_contacts: number;
  my_calendar_events: number;
  upcoming_events: number;
}

export interface DefaultUserStats extends BaseStatistics {
  overview_type: "user";
  my_tasks: number;
  my_assigned_tasks: number;
  my_contacts: number;
}

export type RoleStatistics = 
  | FirmAdminStats 
  | LawyerStats 
  | ClientStats 
  | StaffStats 
  | DefaultUserStats;

export interface StatisticsResponse {
  status: boolean;
  message: string;
  data: RoleStatistics;
  user_role: "super-admin" | "admin" | "lawyer" | "client" | "staff";
  access_level: "super_admin" | "admin" | "user";
}

// Helper type guards
export function isFirmAdminStats(stats: RoleStatistics): stats is FirmAdminStats {
  return stats.overview_type === "admin";
}

export function isLawyerStats(stats: RoleStatistics): stats is LawyerStats {
  return stats.overview_type === "lawyer";
}

export function isClientStats(stats: RoleStatistics): stats is ClientStats {
  return stats.overview_type === "client";
}

export function isStaffStats(stats: RoleStatistics): stats is StaffStats {
  return stats.overview_type === "staff";
}

export function isDefaultUserStats(stats: RoleStatistics): stats is DefaultUserStats {
  return stats.overview_type === "user";
}
